import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/react';
import { useGlobalContext } from '@/store';
import {
  createCheckoutSession,
  getUserCredits,
  consumeUserCredit,
  getStripeProducts,
  StripeProductsResponse,
} from '@/api/payments';

const PaymentGateway = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [credits, setCredits] = useState<number>(0);
  const [loadingCredits, setLoadingCredits] = useState<boolean>(true);
  const [stripeProducts, setStripeProducts] = useState<StripeProductsResponse>({ products: [] });
  const [loadingProducts, setLoadingProducts] = useState<boolean>(true);

  const navigate = useNavigate();
  const { user } = useUser();
  const { patient, setToast } = useGlobalContext();

  const userId = user?.primaryEmailAddress?.emailAddress || patient.email || user?.id || 'user_guest';

  useEffect(() => {
    let isMounted = true;
    if (userId) {
      getUserCredits(userId)
        .then((c) => {
          if (isMounted) setCredits(c);
        })
        .finally(() => {
          if (isMounted) setLoadingCredits(false);
        });
    } else {
      setLoadingCredits(false);
    }
    return () => {
      isMounted = false;
    };
  }, [userId]);

  useEffect(() => {
    let isMounted = true;
    getStripeProducts()
      .then((res) => {
        if (isMounted) setStripeProducts(res);
      })
      .finally(() => {
        if (isMounted) setLoadingProducts(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const handleUseCredit = async () => {
    if (credits < 1) return;
    try {
      setIsLoading(true);
      const res = await consumeUserCredit(userId);

      if (res.success) {
        setToast({ type: 'success', message: 'Credit successfully consumed.' });
        navigate('/results?unlocked=true', { replace: true });
      } else {
        setToast({ type: 'error', message: res.reason || 'Could not consume credit.' });
      }
    } catch (error: unknown) {
      console.error('Error using credit:', error);
      setToast({ type: 'error', message: 'Communication error while using credit.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePurchase = async (quantity: number, paymentType: 'bonus' | 'single', priceId?: string) => {
    try {
      setIsLoading(true);
      if (patient.consultationToken) {
        localStorage.setItem('pending_payment_consultation_token', patient.consultationToken);
      }
      const successUrl = `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${window.location.origin}/cancel`;
      const data = await createCheckoutSession(userId, quantity, paymentType, priceId, successUrl, cancelUrl);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setToast({ type: 'error', message: 'Failed to retrieve payment URL.' });
      }
    } catch (error: unknown) {
      console.error('Error in payment:', error);
      const msg =
        error && typeof error === 'object' && 'response' in error && (error as any).response?.data?.detail
          ? String((error as any).response.data.detail)
          : 'Error processing payment request.';
      setToast({ type: 'error', message: msg });
    } finally {
      setIsLoading(false);
    }
  };

  const paymentsEnabled = import.meta.env.VITE_PAYMENTS_ENABLED !== 'false';

  if (!paymentsEnabled) {
    return (
      <div className="p-6 text-center text-gray-700 bg-yellow-50 rounded-lg max-w-lg mx-auto my-8 border border-yellow-200">
        <h2 className="text-lg font-semibold mb-2">Payment Gateway Disabled</h2>
        <p className="text-sm">Payments are temporarily disabled in the current environment.</p>
      </div>
    );
  }

  const bonusProduct = stripeProducts.bonus || stripeProducts.products.find((p) => p.payment_type === 'bonus');
  const singleProduct = stripeProducts.single || stripeProducts.products.find((p) => p.payment_type === 'single');

  const bonusQuantity = bonusProduct?.quantity || 5;
  const singleQuantity = singleProduct?.quantity || 1;

  const buyCredits = () => handlePurchase(bonusQuantity, 'bonus', bonusProduct?.price_id);
  const paySingleConsultation = () => handlePurchase(singleQuantity, 'single', singleProduct?.price_id);

  return (
    <div className="container mx-auto p-4 max-w-2xl">
      <div className="mb-6 bg-white p-4 rounded-lg border shadow-sm flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Account Balance</h2>
          <p className="text-sm text-gray-600">
            {loadingCredits ? 'Loading credits...' : `You have ${credits} available credit(s).`}
          </p>
        </div>
        {credits > 0 && (
          <button
            onClick={handleUseCredit}
            disabled={isLoading}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-medium text-sm transition-colors"
          >
            {isLoading ? 'Processing...' : 'Use 1 available credit'}
          </button>
        )}
      </div>

      {/* Option 1: Credit Bundle Purchase */}
      <section className="bg-blue-50 rounded-lg p-6 mb-6 border border-blue-100 shadow-sm">
        <h2 className="text-xl font-bold mb-2 text-blue-900">
          Option 1: {bonusProduct?.name || 'Bonus Bundle Payment'}
        </h2>
        <p className="text-sm text-gray-700 mb-4">
          {bonusProduct?.description ||
            `Purchase a package of ${bonusQuantity} credits. They are saved to your account for future consultations, and 1 credit will be used to unlock the current remedy.`}
        </p>

        <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-blue-200">
          <div>
            <h3 className="font-semibold text-gray-800">{bonusQuantity} Credits Bundle</h3>
            <p className="text-green-600 font-bold mt-1">
              {loadingProducts ? 'Loading price...' : bonusProduct?.formatted_price || '6.25 €'}
            </p>
          </div>

          <button
            onClick={buyCredits}
            disabled={isLoading || loadingProducts}
            className={`px-5 py-2.5 rounded-md font-medium text-sm ${
              isLoading || loadingProducts
                ? 'bg-gray-400 cursor-not-allowed text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            } transition-colors`}
          >
            {isLoading ? 'Processing...' : `Purchase (${bonusQuantity} Credits)`}
          </button>
        </div>
      </section>

      {/* Option 2: Single Consultation Payment */}
      <section className="bg-purple-50 rounded-lg p-6 mb-6 border border-purple-100 shadow-sm">
        <h2 className="text-xl font-bold mb-2 text-purple-900">
          Option 2: {singleProduct?.name || 'Single Consultation Payment'}
        </h2>
        <p className="text-sm text-gray-700 mb-4">
          {singleProduct?.description ||
            'Pay only for the current consultation without accumulating credits in your account. The remedy will be displayed immediately.'}
        </p>

        <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-purple-200">
          <div>
            <h3 className="font-semibold text-gray-800">Unlock Current Consultation</h3>
            <p className="text-green-600 font-bold mt-1">
              {loadingProducts ? 'Loading price...' : singleProduct?.formatted_price || '1.99 €'}
            </p>
          </div>

          <button
            onClick={paySingleConsultation}
            disabled={isLoading || loadingProducts}
            className={`px-5 py-2.5 rounded-md font-medium text-sm ${
              isLoading || loadingProducts
                ? 'bg-gray-400 cursor-not-allowed text-white'
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            } transition-colors`}
          >
            {isLoading ? 'Processing...' : `Pay (${singleProduct?.formatted_price || 'Single Consultation'})`}
          </button>
        </div>
      </section>
    </div>
  );
};

export default PaymentGateway;